
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useMessage } from '../contexts/MessageContext';
import { categoriasConfig, getEtiquetasPorCategoria } from '../config/categoriasConfig';
import buildApiUrl from '../config/apiUrlHelper';
import ToggleSwitch from './ToggleSwitch';
import Accordion from './Accordion';
import DonutChart from './DonutChart';

const getDistribucionPorDificultad = (cantidadTotal) => {
  const total = Number.isFinite(cantidadTotal) ? Math.max(1, Math.floor(cantidadTotal)) : 12;
  const pesos = [
    { key: 'facil', value: 5 / 12 },
    { key: 'medio', value: 4 / 12 },
    { key: 'dificil', value: 3 / 12 }
  ];

  const parciales = pesos.map(({ key, value }) => {
    const exacto = total * value;
    const base = Math.floor(exacto);
    return { key, base, resto: exacto - base };
  });

  let restantes = total - parciales.reduce((acc, item) => acc + item.base, 0);
  parciales.sort((a, b) => b.resto - a.resto);

  let i = 0;
  while (restantes > 0) {
    parciales[i % parciales.length].base += 1;
    restantes -= 1;
    i += 1;
  }

  return parciales.reduce((acc, item) => {
    acc[item.key] = item.base;
    return acc;
  }, { facil: 0, medio: 0, dificil: 0 });
};

const InicioConcurso = () => {
  const navigate = useNavigate();
  const { showMessage } = useMessage(); // Obtener la función de mensajes del contexto
  // Estado: categorías y etiquetas seleccionadas
  const [categoriasSeleccionadas, setCategoriasSeleccionadas] = useState(Object.keys(categoriasConfig));
  // { [categoria]: string[] }
  const [etiquetasSeleccionadas, setEtiquetasSeleccionadas] = useState(() => {
    const obj = {};
    Object.keys(categoriasConfig).forEach(cat => {
      obj[cat] = getEtiquetasPorCategoria(cat);
    });
    return obj;
  });
  const [cantidadPreguntas, setCantidadPreguntas] = useState(12);
  const [duracionConcurso, setDuracionConcurso] = useState(0); // en segundos, 0 significa sin límite
  const [preguntasPorDificultad, setPreguntasPorDificultad] = useState(() => getDistribucionPorDificultad(12));
  const [distribucionPreguntas, setDistribucionPreguntas] = useState([]);
  const [cargandoDistribucion, setCargandoDistribucion] = useState(false);

  useEffect(() => {
    setPreguntasPorDificultad(getDistribucionPorDificultad(cantidadPreguntas));
  }, [cantidadPreguntas]);


  // Toggle de categoría
  const handleCategoriaChange = (categoria, checked) => {
    if (checked) {
      setCategoriasSeleccionadas([...categoriasSeleccionadas, categoria]);
      setEtiquetasSeleccionadas(prev => ({
        ...prev,
        [categoria]: getEtiquetasPorCategoria(categoria)
      }));
    } else {
      setCategoriasSeleccionadas(categoriasSeleccionadas.filter(c => c !== categoria));
      setEtiquetasSeleccionadas(prev => {
        const nuevo = { ...prev };
        delete nuevo[categoria];
        return nuevo;
      });
    }
  };

  // Toggle de etiqueta individual
  const handleEtiquetaChange = (categoria, etiqueta, checked) => {
    setEtiquetasSeleccionadas(prev => {
      const actual = prev[categoria] || [];
      let nuevo;
      if (checked) {
        nuevo = [...actual, etiqueta];
      } else {
        nuevo = actual.filter(e => e !== etiqueta);
      }
      return { ...prev, [categoria]: nuevo };
    });
  };

  // Toggle general de etiquetas
  const handleToggleTodasEtiquetas = (categoria, checked) => {
    setEtiquetasSeleccionadas(prev => ({
      ...prev,
      [categoria]: checked ? getEtiquetasPorCategoria(categoria) : []
    }));
  };

  // Obtener la distribución de preguntas para las categorías/etiquetas seleccionadas
  const obtenerDistribucionPreguntas = useCallback(async () => {
    if (categoriasSeleccionadas.length > 0) {
      setCargandoDistribucion(true);
      try {
        // Unificar etiquetas seleccionadas
        const etiquetas = categoriasSeleccionadas.flatMap(cat => etiquetasSeleccionadas[cat] || []);
        const apiUrl = buildApiUrl('obtener_estadisticas_concurso.php');
        const response = await axios.post(apiUrl, {
          categorias: categoriasSeleccionadas,
          etiquetas,
          cantidad: cantidadPreguntas,
          preguntas_por_dificultad: preguntasPorDificultad
        });
        if (response.data.estadisticas) {
          setDistribucionPreguntas(response.data.estadisticas);
        } else {
          setDistribucionPreguntas([]);
        }
      } catch (error) {
        setDistribucionPreguntas([]);
      } finally {
        setCargandoDistribucion(false);
      }
    } else {
      setDistribucionPreguntas([]);
    }
  }, [categoriasSeleccionadas, etiquetasSeleccionadas, cantidadPreguntas, preguntasPorDificultad]);

  // Efecto para obtener la distribución de preguntas cuando cambian las categorías o etiquetas seleccionadas
  useEffect(() => {
    const timer = setTimeout(obtenerDistribucionPreguntas, 300);
    return () => clearTimeout(timer);
  }, [obtenerDistribucionPreguntas]);

  const iniciarConcurso = async () => {
    try {
      // Verificar si hay preguntas de preview almacenadas en sessionStorage
      const previewPreguntas = sessionStorage.getItem('previewPreguntas');
      const previewConfig = sessionStorage.getItem('previewConfig');

      if (previewPreguntas) {
        // Usar las preguntas almacenadas en sessionStorage para garantizar consistencia con la preview
        const preguntas = JSON.parse(previewPreguntas);
        const config = previewConfig ? JSON.parse(previewConfig) : null;

        // Verificar que las preguntas almacenadas coincidan con la configuración actual
        if (config) {
          // Comparar configuraciones para asegurar consistencia
          // Convertir etiquetasSeleccionadas a array plano para comparar
          const etiquetasSeleccionadasArray = Object.values(etiquetasSeleccionadas).flat().sort();
          // Asegurar que config.etiquetas es array antes de sort
          const configEtiquetasArray = Array.isArray(config.etiquetas) ? [...config.etiquetas].sort() : [];
          const configCoincidente = 
            JSON.stringify(config.categorias.sort()) === JSON.stringify(categoriasSeleccionadas.sort()) &&
            JSON.stringify(configEtiquetasArray) === JSON.stringify(etiquetasSeleccionadasArray) &&
            config.cantidad === cantidadPreguntas &&
            JSON.stringify(config.preguntas_por_dificultad) === JSON.stringify(preguntasPorDificultad);

          if (configCoincidente) {
            // Las preguntas almacenadas coinciden con la configuración actual, usarlas directamente
            // Hacer la llamada al backend para crear el concurso con las preguntas almacenadas
            // Esto asegura que se cree un registro válido en la base de datos
            const apiUrl = buildApiUrl('iniciar_concurso.php');
            const response = await axios.post(apiUrl, {
              categorias: categoriasSeleccionadas,
              etiquetas: etiquetasSeleccionadasArray,
              cantidad: cantidadPreguntas,
              duracion: duracionConcurso,
              preguntas_por_dificultad: preguntasPorDificultad,
              // Pasar las preguntas almacenadas para que el backend las use si es posible
              use_preview_questions: true,
              preview_questions: preguntas
            });

            if (response.data.success && response.data.preguntas) {
              // Almacenar las preguntas en sessionStorage para que useQuiz las pueda usar
              sessionStorage.setItem('preguntasConcurso', JSON.stringify(response.data.preguntas));
              // Almacenar la cantidad de preguntas para verificarla después
              sessionStorage.setItem('cantidadPreguntas', cantidadPreguntas.toString());
              // Almacenar la duración del concurso en sessionStorage para que esté disponible en Quiz
              sessionStorage.setItem('duracionConcurso', duracionConcurso.toString());
              // Almacenar el ID del concurso en sessionStorage para poder terminarlo más tarde
              sessionStorage.setItem('concurso_id', response.data.concurso_id.toString());
              // Redirigir al quiz
              navigate('/quiz');
              return; // Salir para evitar la duplicación de lógica
            } else {
              const errorMessage = response.data.error || 'Error desconocido al iniciar el concurso';
              console.error('Error al iniciar concurso (respuesta del servidor):', errorMessage);
              showMessage('Error al iniciar el concurso: ' + errorMessage, 'error');
            }
          }
        }
      }

      // Si no hay preguntas almacenadas o no coinciden con la configuración actual, proceder con la lógica original
      // Convertir etiquetasSeleccionadas a array plano para enviar al backend
      const etiquetasSeleccionadasArray = Object.values(etiquetasSeleccionadas).flat();
      const apiUrl = buildApiUrl('iniciar_concurso.php');
      const response = await axios.post(apiUrl, {
        categorias: categoriasSeleccionadas,
        etiquetas: etiquetasSeleccionadasArray,
        cantidad: cantidadPreguntas,
        duracion: duracionConcurso,
        preguntas_por_dificultad: preguntasPorDificultad
      });
      
      if (response.data.success && response.data.preguntas) {
        // Almacenar las preguntas en sessionStorage para que useQuiz las pueda usar
        sessionStorage.setItem('preguntasConcurso', JSON.stringify(response.data.preguntas));
        // Almacenar la cantidad de preguntas para verificarla después
        sessionStorage.setItem('cantidadPreguntas', cantidadPreguntas.toString());
        // Almacenar la duración del concurso en sessionStorage para que esté disponible en Quiz
        sessionStorage.setItem('duracionConcurso', duracionConcurso.toString());
        // Almacenar el ID del concurso en sessionStorage para poder terminarlo más tarde
        sessionStorage.setItem('concurso_id', response.data.concurso_id.toString());
        // Redirigir al quiz
        navigate('/quiz');
      } else {
        console.error('Error al iniciar concurso:', response.data.error || 'No se recibieron preguntas válidas');
        showMessage(response.data.error || 'Error al iniciar el concurso', 'error');
      }
    } catch (error) {
      console.error('Error al iniciar concurso:', error);
      showMessage('Error al iniciar el concurso', 'error');
    }
  };


  // --- UI ---
  return (
    <div className="dark:bg-gray-800 dark:text-white transition-colors duration-300 grid grid-cols-1 xl:grid-cols-2 gap-5 sm:gap-6 lg:gap-8 overflow-x-hidden">
      {/* Panel principal */}
      <div>
        <h3 className="text-xl font-semibold mb-4 dark:text-white flex items-center gap-2">
          {/* Icono trofeo Flaticon 2639922 SVG exacto */}
          <svg width="26" height="26" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="inline-block align-middle text-yellow-400">
            <path d="M20 8H44V18C44 25.732 38.627 32 32 32C25.373 32 20 25.732 20 18V8Z" stroke="currentColor" strokeWidth="3" strokeLinejoin="round" />
            <path d="M20 12H12C12 20 16 24 22 24" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M44 12H52C52 20 48 24 42 24" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M32 32V42" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
            <path d="M24 54H40" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
            <path d="M26 42H38V46C38 50.418 35.314 54 32 54C28.686 54 26 50.418 26 46V42Z" stroke="currentColor" strokeWidth="3" strokeLinejoin="round" />
            <circle cx="32" cy="20" r="4" fill="currentColor" />
          </svg>
          Iniciar Concurso
        </h3>

        {/* Selección de categorías */}
        <div className="mb-6">
          <h4 className="text-lg font-medium mb-3 dark:text-white">Categorías</h4>
          <div className="grid grid-cols-3 sm:grid-cols-2 lg:grid-cols-3 gap-1.5 sm:gap-3">
            {Object.keys(categoriasConfig)
              .sort((a, b) => categoriasConfig[a].nombre.localeCompare(categoriasConfig[b].nombre, 'es', { sensitivity: 'base' }))
              .map(categoria => (
                <ToggleSwitch
                  key={categoria}
                  checked={categoriasSeleccionadas.includes(categoria)}
                  onChange={checked => handleCategoriaChange(categoria, checked)}
                  label={categoriasConfig[categoria].nombre}
                  className="min-w-0"
                  labelClassName="text-[11px] sm:text-sm leading-tight truncate"
                />
              ))}
          </div>
        </div>


        {/* Agrupador de acordeones de etiquetas */}
        <Accordion title="Etiquetas por categoría" className="mb-6" bg="bg-gray-100 dark:bg-gray-700">
          {categoriasSeleccionadas
            .slice()
            .sort((a, b) => categoriasConfig[a].nombre.localeCompare(categoriasConfig[b].nombre, 'es', { sensitivity: 'base' }))
            .map(categoria => {
              const etiquetas = getEtiquetasPorCategoria(categoria).slice().sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' }));
              const seleccionadas = etiquetasSeleccionadas[categoria] || [];
              const allOn = seleccionadas.length === etiquetas.length;
              const allOff = seleccionadas.length === 0;
              return (
                <Accordion
                  key={categoria}
                  title={categoriasConfig[categoria].nombre}
                  className="mb-2"
                  bg="bg-white dark:bg-gray-800"
                >
                  <div className="mb-2 flex items-center">
                    <ToggleSwitch
                      checked={allOn}
                      onChange={checked => handleToggleTodasEtiquetas(categoria, checked)}
                      label={allOn ? 'Todas activadas' : allOff ? 'Todas desactivadas' : 'Algunas activas'}
                    />
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-1">
                    {etiquetas.map(etiqueta => (
                      <ToggleSwitch
                        key={etiqueta}
                        checked={seleccionadas.includes(etiqueta)}
                        onChange={checked => handleEtiquetaChange(categoria, etiqueta, checked)}
                        label={etiqueta}
                        className="text-xs"
                        labelClassName="text-xs"
                      />
                    ))}
                  </div>
                </Accordion>
              );
            })}
        </Accordion>

        {/* Opciones del concurso en acordeón */}
        <Accordion title="Opciones del Concurso" className="mb-6" bg="bg-gray-100 dark:bg-gray-700">
          <div className="grid grid-cols-2 md:grid-cols-2 gap-3 sm:gap-4 mb-4">
            <div>
              <label className="block text-gray-700 mb-2 dark:text-gray-300">Cantidad de Preguntas</label>
              <input
                type="number"
                min="1"
                max="50"
                value={cantidadPreguntas}
                onChange={e => {
                  const valor = parseInt(e.target.value, 10);
                  if (Number.isNaN(valor)) {
                    setCantidadPreguntas(1);
                    return;
                  }
                  const normalizado = Math.min(50, Math.max(1, valor));
                  setCantidadPreguntas(normalizado);
                }}
                className="w-full p-2 border-gray-300 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white transition-colors duration-300"
              />
            </div>
            <div>
              <label className="block text-gray-700 mb-2 dark:text-gray-300">Duración (segundos)</label>
              <input
                type="number"
                min="0"
                max="3600"
                value={duracionConcurso}
                onChange={e => {
                  const valor = Number(e.target.value);
                  if (!Number.isFinite(valor) || valor <= 0) {
                    setDuracionConcurso(0);
                    return;
                  }

                  setDuracionConcurso(Math.min(3600, Math.floor(valor)));
                }}
                className="w-full p-2 border-gray-300 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white transition-colors duration-300"
              />
              <p className="text-sm text-gray-500 mt-1 dark:text-gray-400">Se aplica a cada pregunta. 0 significa sin límite de tiempo.</p>
            </div>
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 mb-2 dark:text-gray-300">Preguntas por Dificultad</label>
            <div className="grid grid-cols-3 sm:grid-cols-3 gap-2 sm:gap-4">
              <div>
                <span className="block text-sm text-gray-600 dark:text-gray-300">Fácil</span>
                <input
                  type="number"
                  min="0"
                  max={cantidadPreguntas}
                  value={preguntasPorDificultad.facil}
                  onChange={e => setPreguntasPorDificultad({ ...preguntasPorDificultad, facil: parseInt(e.target.value) })}
                  className="w-full p-2 border-gray-300 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white transition-colors duration-300"
                />
              </div>
              <div>
                <span className="block text-sm text-gray-600 dark:text-gray-300">Medio</span>
                <input
                  type="number"
                  min="0"
                  max={cantidadPreguntas}
                  value={preguntasPorDificultad.medio}
                  onChange={e => setPreguntasPorDificultad({ ...preguntasPorDificultad, medio: parseInt(e.target.value) })}
                  className="w-full p-2 border-gray-300 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white transition-colors duration-300"
                />
              </div>
              <div>
                <span className="block text-sm text-gray-600 dark:text-gray-300">Difícil</span>
                <input
                  type="number"
                  min="0"
                  max={cantidadPreguntas}
                  value={preguntasPorDificultad.dificil}
                  onChange={e => setPreguntasPorDificultad({ ...preguntasPorDificultad, dificil: parseInt(e.target.value) })}
                  className="w-full p-2 border border-gray-300 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white transition-colors duration-300"
                />
              </div>
            </div>
          </div>
        </Accordion>

        <button
          onClick={iniciarConcurso}
          disabled={categoriasSeleccionadas.length === 0 || cargandoDistribucion}
          className="w-full sm:w-auto bg-green-600 text-white py-3 px-6 rounded hover:bg-green-700 transition-colors duration-300 font-semibold disabled:opacity-50 disabled:cursor-not-allowed dark:bg-green-700 dark:hover:bg-green-800"
        >
          Iniciar Concurso
        </button>
      </div>

      {/* Panel derecho: gráfico circular */}
      <div className="relative flex flex-col items-center justify-start pt-8 md:pt-0">
        <button
          type="button"
          onClick={obtenerDistribucionPreguntas}
          disabled={cargandoDistribucion || categoriasSeleccionadas.length === 0}
          title="Recargar concurso"
          aria-label="Recargar concurso"
          className="absolute left-0 top-0 text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            className={`w-6 h-6 ${cargandoDistribucion ? 'animate-spin' : ''}`}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
          </svg>
        </button>
        <h4 className="text-lg font-medium mb-3 dark:text-white">Distribución de Preguntas</h4>
        {cargandoDistribucion ? (
          <div className="flex flex-col items-center justify-center h-72">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
            <p className="mt-2 dark:text-white">Calculando distribución...</p>
          </div>
        ) : distribucionPreguntas.length > 0 ? (
          <DonutChart
            data={distribucionPreguntas.map(item => ({
              label: categoriasConfig[item.categoria]?.nombre || item.categoria,
              value: item.cantidad,
              color: categoriasConfig[item.categoria]?.color || '#8884d8',
            }))}
            title="Próximo concurso"
          />
        ) : (
          <p className="text-gray-600 dark:text-gray-300 italic">No hay suficientes preguntas para los filtros seleccionados.</p>
        )}
      </div>
    </div>
  );

  // Bloque duplicado eliminado, solo queda el return principal
};

export default InicioConcurso;
